import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  ArrowLeft,
  Loader2,
  CheckCircle,
  AlertCircle,
  Download,
  MessageSquare,
  BookOpen,
  Shield,
  Heart,
  Activity,
  Users,
  Clock,
  Home as HomeIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { dailyHelpService } from '../Services/dailyHelpService';

const QuizPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('intro'); // intro, quiz, results
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});
  const [ageGroup, setAgeGroup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  // Questions adaptatives selon l'âge
  const getQuestions = () => {
    const baseQuestion = {
      question: "Quel est l'âge de la personne concernée ?",
      type: 'radio',
      options: [
        { value: 'child', label: '0-12 ans (Enfant)' },
        { value: 'teen', label: '13-25 ans (Adolescent/Jeune adulte)' },
        { value: 'adult', label: '26-60 ans (Adulte)' },
        { value: 'senior', label: 'Plus de 60 ans (Senior)' },
      ],
    };

    const childQuestions = [
      baseQuestion,
      {
        question: 'Quelles sont vos principales préoccupations ?',
        type: 'checkbox',
        options: ['Sommeil', 'Alimentation', 'Développement', 'Comportement', 'Santé générale'],
      },
      {
        question: "L'enfant a-t-il des besoins particuliers ?",
        type: 'radio',
        options: [
          'Non',
          'Oui, allergie ou intolérance',
          'Oui, suivi médical régulier',
          'Oui, handicap ou trouble',
        ],
      },
      {
        question: "Comment se passe la vie à l'école/crèche ?",
        type: 'radio',
        options: [
          'Très bien',
          'Bien avec quelques difficultés',
          'Difficultés importantes',
          'Non scolarisé(e)',
        ],
      },
      {
        question: 'Quel est votre besoin principal ?',
        type: 'checkbox',
        options: [
          'Conseils nutrition',
          'Suivi développement',
          'Gestion des émotions',
          'Prévention santé',
          'Accompagnement quotidien',
        ],
      },
    ];

    const adultQuestions = [
      baseQuestion,
      {
        question: 'Quelles sont vos principales préoccupations de santé ?',
        type: 'checkbox',
        options: [
          'Stress/Anxiété',
          'Sommeil',
          'Douleurs chroniques',
          'Poids/Nutrition',
          'Énergie/Fatigue',
          'Prévention',
        ],
      },
      {
        question: "Quel est votre niveau d'activité physique ?",
        type: 'radio',
        options: [
          'Très actif (sport régulier)',
          'Modéré (marche quotidienne)',
          'Peu actif',
          'Sédentaire',
        ],
      },
      {
        question: 'Quelle est votre situation professionnelle ?',
        type: 'radio',
        options: [
          'En activité temps plein',
          'Temps partiel',
          'Sans emploi',
          'Étudiant(e)',
          'En arrêt maladie',
        ],
      },
      {
        question: 'Comment gérez-vous votre équilibre vie pro/perso ?',
        type: 'radio',
        options: ['Très bien équilibré', 'Plutôt bien', 'Difficile', 'Épuisement/Burn-out'],
      },
      {
        question: 'Quelles sont vos priorités santé ?',
        type: 'checkbox',
        options: [
          'Mieux dormir',
          'Perdre/prendre du poids',
          'Gérer le stress',
          'Bouger plus',
          'Mieux manger',
          'Prévenir les maladies',
        ],
      },
    ];

    const seniorQuestions = [
      baseQuestion,
      {
        question: 'Quelles sont vos principales préoccupations ?',
        type: 'checkbox',
        options: [
          'Mobilité',
          'Mémoire',
          'Douleurs',
          'Isolement social',
          'Autonomie quotidienne',
          'Sécurité domicile',
        ],
      },
      {
        question: 'Comment évaluez-vous votre mobilité ?',
        type: 'radio',
        options: [
          'Aucune difficulté',
          'Légères difficultés',
          "Besoin d'une aide technique",
          'Mobilité très réduite',
        ],
      },
      {
        question: 'Vivez-vous seul(e) ?',
        type: 'radio',
        options: ['Oui, seul(e)', 'Avec conjoint(e)', 'Avec famille', 'En établissement'],
      },
      {
        question: 'Avez-vous déjà chuté à domicile ?',
        type: 'radio',
        options: ['Jamais', 'Une fois', 'Plusieurs fois', 'Récemment (< 6 mois)'],
      },
      {
        question: 'De quoi avez-vous besoin en priorité ?',
        type: 'checkbox',
        options: [
          'Sécuriser mon logement',
          'Aide quotidienne',
          'Lien social',
          'Suivi médical',
          'Aides financières',
          'Maintenir mon autonomie',
        ],
      },
    ];

    if (!ageGroup) return [baseQuestion];
    if (ageGroup === 'child') return childQuestions;
    if (ageGroup === 'teen' || ageGroup === 'adult') return adultQuestions;
    return seniorQuestions;
  };

  const questions = getQuestions();
  const progress = questions.length > 0 ? (currentQuestion / questions.length) * 100 : 0;
  const handleNext = () => {
    // Première question = définir l'âge et recharger
    if (currentQuestion === 0 && responses[0]) {
      setAgeGroup(responses[0]);
      setCurrentQuestion(1); // Passe à la question 2
      return; // Important : stoppe ici pour que les nouvelles questions se chargent
    }

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Générer résultats détaillés selon l'âge
      const detailedResults = generateDetailedResults(ageGroup, responses);
      setResults(detailedResults);
      setStep('results');
      toast.success('Analyse complétée !');
    } catch (error) {
      console.error('Erreur:', error);
      toast.error("Erreur lors de l'analyse");
    } finally {
      setLoading(false);
    }
  };

  // Fonction de génération des résultats détaillés
  const generateDetailedResults = (age, responses) => {
    const results = {
      ageGroup: age,
      niveau: '',
      score: 0,
      syntheseGenerale: '',
      pointsForts: [],
      pointsAttention: [],
      priorites: [],
      recommendations: {
        immediates: [],
        courtTerme: [],
        longTerme: [],
      },
      ressources: [],
      suiviRecommande: '',
      alertes: [],
    };

    // ENFANT (0-12 ans)
    if (age === 'child') {
      const concerns = responses[1] || [];
      const specialNeeds = responses[2];
      const school = responses[3];
      const mainNeeds = responses[4] || [];

      // Synthèse
      results.niveau = "Profil de développement de l'enfant";
      results.syntheseGenerale = `Analyse basée sur ${concerns.length} préoccupation(s) principale(s). `;

      // Points forts
      if (!concerns.includes('Développement')) {
        results.pointsForts.push('Développement général dans les normes');
      }
      if (school === 'Très bien') {
        results.pointsForts.push('Bonne intégration scolaire/crèche');
      }
      if (specialNeeds === 'Non') {
        results.pointsForts.push('Aucun besoin particulier identifié');
      }

      // Points d'attention
      if (concerns.includes('Sommeil')) {
        results.pointsAttention.push({
          titre: 'Sommeil perturbé',
          description:
            "Le sommeil est essentiel au développement cognitif et physique de l'enfant.",
          impact: 'Peut affecter concentration, croissance et immunité',
          solutions: ['Routine régulière', 'Environnement calme', 'Limiter écrans 2h avant'],
        });
      }

      if (concerns.includes('Alimentation')) {
        results.pointsAttention.push({
          titre: 'Alimentation à surveiller',
          description: 'Une nutrition équilibrée est cruciale pour la croissance.',
          impact: 'Influence développement physique et cognitif',
          solutions: ['Diversification progressive', 'Repas en famille', 'Éviter le forcing'],
        });
      }

      if (concerns.includes('Comportement')) {
        results.pointsAttention.push({
          titre: 'Difficultés comportementales',
          description: "Les comportements sont des formes d'expression chez l'enfant.",
          impact: 'Peut révéler un besoin non satisfait ou un inconfort',
          solutions: ['Observer les déclencheurs', 'Communication adaptée', 'Routine stable'],
        });
      }

      if (school !== 'Très bien') {
        results.pointsAttention.push({
          titre: 'Adaptation scolaire à accompagner',
          description: "L'environnement scolaire peut être source de stress.",
          impact: 'Influence confiance en soi et apprentissages',
          solutions: ['Dialogue avec enseignants', 'Activités extra-scolaires', 'Temps de qualité'],
        });
      }

      // Priorités
      if (specialNeeds !== 'Non') {
        results.priorites.push({
          niveau: 'Haute',
          titre: 'Suivi spécialisé recommandé',
          description: 'Les besoins particuliers nécessitent un accompagnement adapté.',
          actions: [
            'Consulter le pédiatre pour orientation',
            'Bilan avec spécialiste si nécessaire',
            "Coordination avec l'école",
            'Dossier MDPH si handicap reconnu',
          ],
          delai: 'Dans les 2 mois',
        });
      }

      if (mainNeeds.includes('Conseils nutrition')) {
        results.priorites.push({
          niveau: 'Moyenne',
          titre: "Optimiser l'alimentation",
          description: "Équilibre nutritionnel adapté à l'âge et activité.",
          actions: [
            'Consultation diététicienne pédiatrique',
            'Introduire 5 fruits/légumes par jour',
            'Repas variés et colorés',
            'Éviter produits ultra-transformés',
          ],
          delai: 'Progressif sur 3 mois',
        });
      }

      if (mainNeeds.includes('Suivi développement')) {
        results.priorites.push({
          niveau: 'Moyenne',
          titre: 'Suivi développemental régulier',
          description: "Vérifier l'atteinte des étapes clés du développement.",
          actions: [
            'Bilans pédiatriques réguliers (tous les 6 mois)',
            'Carnet de santé à jour',
            "Stimulation adaptée à l'âge",
            'Activités motrices et sensorielles',
          ],
          delai: 'Continu',
        });
      }

      // Recommandations immédiates
      results.recommendations.immediates = [
        "📋 Tenir un journal d'observation (sommeil, alimentation, comportement)",
        '👨‍⚕️ Prendre RDV pédiatre si inquiétude',
        '📚 Créer une routine stable et rassurante',
        "🎨 Encourager l'expression par le jeu",
      ];

      // Court terme
      results.recommendations.courtTerme = [
        "Évaluer la nécessité d'un bilan avec spécialiste (orthophoniste, psychomotricien)",
        "Optimiser l'environnement de sommeil",
        "Diversifier l'alimentation progressivement",
        'Renforcer le lien parent-enfant par des activités partagées',
      ];

      // Long terme
      results.recommendations.longTerme = [
        'Suivi régulier des vaccinations et bilans de santé',
        'Favoriser autonomie et confiance en soi',
        'Préparer les transitions (entrée école, déménagement)',
        'Maintenir dialogue ouvert avec équipe éducative',
      ];

      // Ressources
      results.ressources = [
        {
          titre: 'PMI (Protection Maternelle et Infantile)',
          description: 'Consultations gratuites, suivi développement',
        },
        { titre: 'Pédiatre de ville', description: 'Suivi médical régulier' },
        {
          titre: "CAMSP (Centre d'Action Médico-Sociale Précoce)",
          description: 'Si difficultés développementales',
        },
        { titre: 'Psychomotricien', description: 'Troubles moteurs, coordination' },
        { titre: 'Orthophoniste', description: 'Retard de langage' },
      ];

      results.suiviRecommande = "Pédiatre tous les 6 mois jusqu'à 6 ans, puis annuellement";

      // Alertes
      if (concerns.length >= 3) {
        results.alertes.push(
          '⚠️ Plusieurs préoccupations identifiées : consultation pédiatrique recommandée dans les 4 semaines'
        );
      }
    }

    // ADULTE (13-60 ans)
    else if (age === 'adult' || age === 'teen') {
      const concerns = responses[1] || [];
      const activity = responses[2];
      const workLife = responses[3];
      const balance = responses[4];
      const priorities = responses[5] || [];

      results.niveau = age === 'teen' ? 'Profil santé jeune adulte' : 'Profil santé adulte';

      // Calcul score bien-être
      let score = 70;
      if (concerns.length <= 2) score += 10;
      if (activity === 'Très actif (sport régulier)') score += 15;
      if (balance === 'Très bien équilibré') score += 10;
      else if (balance === 'Épuisement/Burn-out') score -= 30;

      results.score = Math.max(0, Math.min(100, score));
      results.syntheseGenerale = `Score de bien-être général : ${results.score}/100. `;

      // Points forts
      if (activity.includes('Très actif') || activity.includes('Modéré')) {
        results.pointsForts.push('Activité physique régulière maintenue');
      }
      if (balance === 'Très bien équilibré') {
        results.pointsForts.push('Bon équilibre vie professionnelle/personnelle');
      }
      if (concerns.length === 0) {
        results.pointsForts.push('Aucune préoccupation majeure identifiée');
      }

      // Points d'attention détaillés
      if (concerns.includes('Stress/Anxiété')) {
        results.pointsAttention.push({
          titre: 'Gestion du stress à améliorer',
          description: 'Le stress chronique impacte santé physique et mentale.',
          impact:
            'Risque : troubles cardiovasculaires, immunitaires, digestifs, troubles du sommeil',
          solutions: [
            'Techniques de relaxation (cohérence cardiaque, méditation)',
            'Activité physique régulière (30min/jour)',
            'Identifier et réduire les sources de stress',
            'Thérapie cognitivo-comportementale si besoin',
          ],
        });
      }

      if (concerns.includes('Sommeil')) {
        results.pointsAttention.push({
          titre: 'Qualité du sommeil insuffisante',
          description: 'Le sommeil est essentiel à la récupération et santé globale.',
          impact: 'Affecte concentration, humeur, métabolisme, immunité',
          solutions: [
            'Horaires réguliers (coucher/lever)',
            'Éviter écrans 1h avant coucher',
            'Chambre fraîche (18-19°C), sombre',
            'Éviter caféine après 16h',
            'Consultation si troubles persistants',
          ],
        });
      }

      if (concerns.includes('Douleurs chroniques')) {
        results.pointsAttention.push({
          titre: 'Douleurs chroniques présentes',
          description: 'Les douleurs persistantes nécessitent une évaluation médicale.',
          impact: 'Altère qualité de vie, sommeil, moral',
          solutions: [
            'Consultation médicale pour diagnostic',
            'Kinésithérapie si douleurs musculo-squelettiques',
            'Activité physique adaptée',
            'Gestion du stress (amplifie la douleur)',
          ],
        });
      }

      if (concerns.includes('Poids/Nutrition')) {
        results.pointsAttention.push({
          titre: 'Équilibre nutritionnel à optimiser',
          description: "L'alimentation influence énergie, santé et bien-être.",
          impact: 'Risque de carences, fatigue, maladies métaboliques',
          solutions: [
            'Bilan nutritionnel avec diététicien',
            'Alimentation variée et équilibrée',
            'Hydratation suffisante (1,5-2L/jour)',
            'Limiter produits transformés',
          ],
        });
      }

      if (balance === 'Épuisement/Burn-out') {
        results.pointsAttention.push({
          titre: "⚠️ Signes d'épuisement professionnel",
          description: 'Le burn-out nécessite une prise en charge rapide.',
          impact: 'Risque de dépression, troubles physiques graves',
          solutions: [
            'Consultation médecin traitant URGENT',
            'Arrêt de travail si nécessaire',
            'Suivi psychologique',
            'Réévaluation charge de travail',
          ],
        });
      }

      if (activity === 'Sédentaire') {
        results.pointsAttention.push({
          titre: 'Sédentarité excessive',
          description: "L'inactivité physique est un facteur de risque majeur.",
          impact: 'Risque cardiovasculaire, diabète, obésité, dépression',
          solutions: [
            'Commencer progressivement (10min/jour)',
            'Marche quotidienne',
            "Monter escaliers plutôt qu'ascenseur",
            'Sport adapté et plaisant',
          ],
        });
      }

      // Priorités
      if (balance === 'Épuisement/Burn-out') {
        results.priorites.push({
          niveau: 'Haute - URGENT',
          titre: "Prise en charge de l'épuisement",
          description: 'Situation nécessitant une intervention rapide.',
          actions: [
            'Consultation médecin traitant dans la semaine',
            'Arrêt de travail si nécessaire',
            'Suivi psychologique spécialisé',
            'Identifier et éliminer facteurs de stress',
          ],
          delai: 'Immédiat',
        });
      }

      if (priorities.includes('Gérer le stress')) {
        results.priorites.push({
          niveau: 'Haute',
          titre: 'Mise en place de stratégies anti-stress',
          description: "Techniques éprouvées pour réduire l'anxiété.",
          actions: [
            'Cohérence cardiaque 3x/jour (5min)',
            'Sport régulier (libère endorphines)',
            'Temps pour soi quotidien',
            'Thérapie si anxiété persistante',
          ],
          delai: 'À débuter cette semaine',
        });
      }

      if (priorities.includes('Mieux dormir')) {
        results.priorites.push({
          niveau: 'Moyenne',
          titre: "Amélioration de l'hygiène de sommeil",
          description: 'Le sommeil réparateur se prépare.',
          actions: [
            'Routine pré-sommeil relaxante',
            'Horaires fixes',
            'Environnement optimal',
            'Éviter excitants le soir',
            'Consultation si insomnie > 3 semaines',
          ],
          delai: 'Progressif sur 1 mois',
        });
      }

      if (priorities.includes('Bouger plus')) {
        results.priorites.push({
          niveau: 'Moyenne',
          titre: "Reprise progressive d'une activité physique",
          description: "L'OMS recommande 150min/semaine d'activité modérée.",
          actions: [
            'Commencer par marche 20min/jour',
            'Trouver activité plaisante (danse, vélo, natation)',
            'Augmenter progressivement',
            'Bilan médical si >40ans et sédentaire',
          ],
          delai: 'Sur 2-3 mois',
        });
      }

      // Recommandations
      results.recommendations.immediates = [
        '📋 Check-up médical annuel (bilan sanguin, tension)',
        '💧 Hydratation 1,5-2L/jour',
        '🥗 5 fruits et légumes par jour minimum',
        "🚶 30min d'activité physique quotidienne",
      ];

      results.recommendations.courtTerme = [
        'Consultation dentiste (détartrage annuel)',
        'Dépistages selon âge (mammographie, coloscopie si >50ans)',
        'Vaccins à jour (rappel tétanos tous les 20ans)',
        "Bilan de fertilité si projet d'enfant",
      ];

      results.recommendations.longTerme = [
        'Prévention cardiovasculaire (alimentation, sport, gestion stress)',
        'Maintien lien social et activités stimulantes',
        'Gestion du poids dans normes IMC',
        'Prévention ostéoporose (calcium, vitamine D, sport)',
      ];

      // Ressources
      results.ressources = [
        { titre: 'Médecin traitant', description: 'Suivi global, dépistages' },
        { titre: 'Psychologue', description: 'Gestion stress, anxiété, burn-out' },
        { titre: 'Diététicien', description: 'Rééquilibrage alimentaire' },
        { titre: 'Coach sportif', description: 'Reprise activité physique adaptée' },
        { titre: 'Médecine du travail', description: 'Problématiques professionnelles' },
      ];

      results.suiviRecommande =
        'Médecin traitant 1x/an minimum, plus fréquent si pathologie chronique';

      // Alertes
      if (balance === 'Épuisement/Burn-out') {
        results.alertes.push(
          "🚨 URGENT : Signes d'épuisement détectés - Consultation médicale dans la semaine impérative"
        );
      }
      if (concerns.includes('Douleurs chroniques')) {
        results.alertes.push(
          '⚠️ Douleurs chroniques : nécessitent évaluation médicale si non prises en charge'
        );
      }
    }

    // SENIOR (60+ ans)
    else if (age === 'senior') {
      const concerns = responses[1] || [];
      const mobility = responses[2];
      const living = responses[3];
      const falls = responses[4];
      const needs = responses[5] || [];

      results.niveau = 'Profil autonomie senior';

      // Score autonomie
      let score = 80;
      if (mobility === 'Aucune difficulté') score += 15;
      else if (mobility === 'Mobilité très réduite') score -= 30;
      if (falls === 'Jamais') score += 10;
      else if (falls === 'Récemment (< 6 mois)') score -= 20;
      if (!concerns.includes('Autonomie quotidienne')) score += 10;

      results.score = Math.max(0, Math.min(100, score));
      results.syntheseGenerale = `Score d'autonomie : ${results.score}/100. `;

      // Points forts
      if (mobility === 'Aucune difficulté') {
        results.pointsForts.push('Mobilité préservée');
      }
      if (falls === 'Jamais') {
        results.pointsForts.push('Aucune chute, bon équilibre');
      }
      if (living !== 'Seul(e)') {
        results.pointsForts.push("Présence d'un entourage");
      }

      // Points d'attention
      if (concerns.includes('Mobilité')) {
        results.pointsAttention.push({
          titre: 'Difficultés de déplacement',
          description: "La mobilité est clé pour maintenir l'autonomie.",
          impact: "Risque d'isolement, perte d'autonomie, chutes",
          solutions: [
            'Kinésithérapie pour renforcement musculaire',
            'Aides techniques (canne, déambulateur)',
            "Aménagement domicile (barres d'appui)",
            'Activité physique adaptée (gym douce)',
          ],
        });
      }

      if (concerns.includes('Mémoire')) {
        results.pointsAttention.push({
          titre: 'Troubles de la mémoire',
          description: "Des oublis peuvent être normaux avec l'âge, mais à surveiller.",
          impact: 'Risque de confusion, erreurs médicamenteuses, isolement',
          solutions: [
            'Consultation mémoire (neurologue, gériatre)',
            'Stimulation cognitive (lecture, jeux, social)',
            'Pilulier organisé',
            'Bilan complet si aggravation',
          ],
        });
      }

      if (concerns.includes('Isolement social')) {
        results.pointsAttention.push({
          titre: "Risque d'isolement social",
          description: 'Le lien social est protecteur pour santé physique et mentale.',
          impact: 'Dépression, déclin cognitif accéléré',
          solutions: [
            'Activités associatives (club seniors)',
            'Portage repas (lien social quotidien)',
            'Aide à domicile',
            'Téléassistance pour sécurité',
          ],
        });
      }

      if (falls !== 'Jamais') {
        results.pointsAttention.push({
          titre: 'Antécédent(s) de chute',
          description: 'Une chute augmente le risque de récidive.',
          impact: 'Fractures, perte de confiance, syndrome post-chute',
          solutions: [
            'Évaluation gériatrique (équilibre, vue, médicaments)',
            'Sécurisation du domicile',
            'Chaussures adaptées',
            'Téléalarme',
            'Activité physique (tai-chi, équilibre)',
          ],
        });
      }

      if (mobility === 'Mobilité très réduite') {
        results.pointsAttention.push({
          titre: '⚠️ Mobilité très limitée',
          description: 'Situation nécessitant accompagnement renforcé.',
          impact: "Perte d'autonomie rapide, escarres, dénutrition",
          solutions: [
            'Évaluation médico-sociale complète',
            'Aides humaines quotidiennes',
            'Fauteuil roulant si besoin',
            'Kinésithérapie à domicile',
            "Dossier APA (Allocation Personnalisée d'Autonomie)",
          ],
        });
      }

      // Priorités
      if (falls === 'Récemment (< 6 mois)') {
        results.priorites.push({
          niveau: 'Haute',
          titre: 'Prévention des chutes',
          description: 'Chute récente = facteur de risque élevé de récidive.',
          actions: [
            'Consultation gériatre/médecin traitant',
            "Bilan de l'équilibre (kiné, podologue)",
            'Sécurisation domicile URGENTE',
            'Téléalarme',
            'Revoir traitements (certains favorisent chutes)',
          ],
          delai: 'Dans le mois',
        });
      }

      if (needs.includes('Sécuriser mon logement')) {
        results.priorites.push({
          niveau: 'Haute',
          titre: 'Aménagement sécuritaire du domicile',
          description: 'Un logement adapté prévient accidents et facilite autonomie.',
          actions: [
            'Visite ergothérapeute à domicile',
            "Installation barres d'appui (salle de bain, WC, couloirs)",
            'Retirer tapis et fils au sol',
            'Éclairage nocturne renforcé',
            'Douche de plain-pied si possible',
            'Aides financières possibles (ANAH, APA)',
          ],
          delai: '2-3 mois',
        });
      }

      if (needs.includes('Aide quotidienne')) {
        results.priorites.push({
          niveau: 'Moyenne',
          titre: "Mise en place d'aides à domicile",
          description: 'Les aides professionnelles maintiennent autonomie et sécurité.',
          actions: [
            'Évaluation des besoins (CLIC, CCAS)',
            "Dossier APA (Allocation Personnalisée d'Autonomie)",
            "Service d'aide à domicile (ménage, courses)",
            'Portage repas si besoin',
            'SSIAD (Soins Infirmiers À Domicile) si soins',
          ],
          delai: '1-2 mois',
        });
      }

      if (needs.includes('Lien social')) {
        results.priorites.push({
          niveau: 'Moyenne',
          titre: 'Renforcement du lien social',
          description: 'Maintenir les contacts sociaux préserve moral et cognition.',
          actions: [
            'Inscription club seniors, associations',
            'Activités culturelles (bibliothèque, sorties)',
            'Bénévolat si santé le permet',
            'Téléassistance (contact quotidien)',
            'Accueil de jour 1-2x/semaine',
          ],
          delai: 'Progressif',
        });
      }

      // Recommandations
      results.recommendations.immediates = [
        '📋 Révision des traitements avec médecin (interactions, posologies)',
        '👁️ Contrôle de la vue annuel',
        '🦷 Suivi dentaire (santé bucco-dentaire = santé générale)',
        '💊 Pilulier organisé pour éviter oublis',
      ];

      results.recommendations.courtTerme = [
        'Évaluation gériatrique complète si non faite',
        'Bilan orthophoniste si troubles déglutition',
        'Podologue pour soins pieds (prévention chutes)',
        'Vaccination grippe et pneumocoque à jour',
        'Dépistages cancers selon recommandations',
      ];

      results.recommendations.longTerme = [
        'Maintien activité physique adaptée (marche, gym douce)',
        'Stimulation cognitive quotidienne',
        'Alimentation riche en protéines et calcium',
        "Réévaluation régulière de l'autonomie (tous les 6 mois)",
        'Anticipation des besoins futurs (tutelle, logement adapté)',
      ];

      // Ressources
      results.ressources = [
        { titre: 'Médecin traitant / Gériatre', description: 'Suivi médical global' },
        { titre: 'CLIC / CCAS', description: 'Information et orientation aides sociales' },
        { titre: 'Ergothérapeute', description: 'Aménagement domicile, aides techniques' },
        { titre: 'Kinésithérapeute', description: 'Maintien mobilité, prévention chutes' },
        { titre: "Services d'aide à domicile", description: 'Ménage, courses, repas' },
        { titre: 'SSIAD', description: 'Soins infirmiers à domicile' },
        { titre: 'Téléassistance', description: 'Sécurité 24h/24' },
        { titre: 'Accueil de jour', description: 'Activités, lien social, répit aidants' },
      ];

      results.suiviRecommande = 'Médecin traitant tous les 3 mois, gériatre annuellement';

      // Alertes
      if (falls === 'Récemment (< 6 mois)') {
        results.alertes.push(
          '🚨 Chute récente : Risque élevé de récidive - Sécurisation domicile et bilan médical URGENT'
        );
      }
      if (mobility === 'Mobilité très réduite' && living === 'Oui, seul(e)') {
        results.alertes.push(
          '⚠️ Situation à risque : Mobilité réduite + Vie seule = Aides humaines quotidiennes recommandées rapidement'
        );
      }
      if (concerns.includes('Mémoire') && concerns.includes('Autonomie quotidienne')) {
        results.alertes.push(
          '⚠️ Troubles cognitifs + Perte autonomie : Consultation mémoire spécialisée conseillée'
        );
      }
    }

    return results;
  };

  // INTRO
  if (step === 'intro') {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Button
            onClick={() => navigate('/daily-help')}
            className="mb-6
            text-slate-700 dark:text-slate-300
            border-slate-300 dark:border-slate-700
            hover:bg-slate-100 dark:hover:bg-slate-800
            hover:text-slate-900 dark:hover:text-white
            transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Button>

          <Card className="p-8 md:p-12 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950/30 dark:to-purple-950/30 border-2 border-blue-200 dark:border-blue-800">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Heart className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Bilan de Santé Personnalisé
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                Répondez à quelques questions pour recevoir des recommandations adaptées à votre
                situation
              </p>
            </div>

            {/* Disclaimer */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-800 rounded-xl p-6 mb-8">
              <div className="flex gap-3">
                <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-2">
                    ⚠️ Important : Ceci n'est pas un diagnostic médical
                  </h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Ce questionnaire fournit des <strong>informations générales</strong> et ne
                    remplace en aucun cas une consultation médicale. En cas de symptômes ou de
                    doute, consultez un professionnel de santé.
                  </p>
                </div>
              </div>
            </div>

            {/* Bénéfices */}
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="text-center p-4">
                <Activity className="w-8 h-8 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">5 minutes</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Questionnaire rapide</p>
              </div>
              <div className="text-center p-4">
                <Shield className="w-8 h-8 text-green-600 dark:text-green-400 mx-auto mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  100% confidentiel
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Données sécurisées</p>
              </div>
              <div className="text-center p-4">
                <BookOpen className="w-8 h-8 text-purple-600 dark:text-purple-400 mx-auto mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  Conseils personnalisés
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Adaptés à votre profil</p>
              </div>
            </div>

            <Button
              onClick={() => setStep('quiz')}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white text-lg py-6 shadow-lg"
            >
              Commencer l'évaluation
            </Button>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // QUIZ
  if (step === 'quiz') {
    const current = questions[currentQuestion];

    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto px-4 py-8">
          <Button
            onClick={() => {
              if (currentQuestion === 0) {
                setStep('intro');
              } else {
                setCurrentQuestion(currentQuestion - 1);
              }
            }}
            variant="outline"
            className="mb-6
            text-slate-700 dark:text-slate-300
            border-slate-300 dark:border-slate-700
            hover:bg-slate-100 dark:hover:bg-slate-800
            hover:text-slate-900 dark:hover:text-white
            transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {currentQuestion === 0 ? 'Retour' : 'Question précédente'}
          </Button>

          <Card className="p-8">
            {/* Progress */}
            <div className="mb-8">
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                <span>
                  Question {currentQuestion + 1} sur {questions.length}
                </span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-purple-600 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Question */}
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
              {current.question}
            </h2>

            {/* Options */}
            <div className="space-y-3 mb-8">
              {current.options.map((option, idx) => {
                const optionValue = typeof option === 'object' ? option.value : option;
                const optionLabel = typeof option === 'object' ? option.label : option;

                return (
                  <label
                    key={idx}
                    className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      current.type === 'radio' && responses[currentQuestion] === optionValue
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                        : current.type === 'checkbox' &&
                            responses[currentQuestion]?.includes(optionValue)
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type={current.type}
                      value={optionValue}
                      checked={
                        current.type === 'radio'
                          ? responses[currentQuestion] === optionValue
                          : responses[currentQuestion]?.includes(optionValue)
                      }
                      onChange={(e) => {
                        if (current.type === 'radio') {
                          setResponses({ ...responses, [currentQuestion]: optionValue });
                        } else {
                          const current = responses[currentQuestion] || [];
                          const updated = e.target.checked
                            ? [...current, optionValue]
                            : current.filter((o) => o !== optionValue);
                          setResponses({ ...responses, [currentQuestion]: updated });
                        }
                      }}
                      className="w-5 h-5"
                    />
                    <span className="text-gray-900 dark:text-gray-100">{optionLabel}</span>
                  </label>
                );
              })}
            </div>

            {/* Navigation */}
            <Button
              onClick={handleNext}
              disabled={
                !responses[currentQuestion] ||
                (current.type === 'checkbox' && responses[currentQuestion]?.length === 0) ||
                loading
              }
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : currentQuestion === questions.length - 1 ? (
                'Voir mes résultats'
              ) : (
                'Question suivante'
              )}
            </Button>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // RESULTS
  if (step === 'results' && results) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl mx-auto px-4 py-8">
          <Button
            onClick={() => navigate('/daily-help')}
            variant="outline"
            className="mb-6
            text-slate-700 dark:text-slate-300
            border-slate-300 dark:border-slate-700
            hover:bg-slate-100 dark:hover:bg-slate-800
            hover:text-slate-900 dark:hover:text-white
            transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Button>

          {/* Header */}
          <Card className="p-8 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border-2 border-green-200 dark:border-green-800 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400 mb-3" />
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  {results.niveau}
                </h1>
                <p className="text-lg text-gray-700 dark:text-gray-300">
                  {results.syntheseGenerale}
                </p>
              </div>
              {results.score > 0 && (
                <div className="text-center">
                  <div className="text-6xl font-bold bg-gradient-to-br from-green-600 to-emerald-600 bg-clip-text text-transparent">
                    {results.score}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">/ 100</div>
                </div>
              )}
            </div>
          </Card>

          {/* Alertes */}
          {results.alertes && results.alertes.length > 0 && (
            <div className="mb-6 space-y-3">
              {results.alertes.map((alerte, idx) => (
                <Card
                  key={idx}
                  className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-800"
                >
                  <p className="text-red-800 dark:text-red-300 font-medium">{alerte}</p>
                </Card>
              ))}
            </div>
          )}

          {/* Disclaimer */}
          <Card className="p-4 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-800 mb-6">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <strong>Rappel :</strong> Cette analyse est informative. Les recommandations ne
                remplacent pas un avis médical. Consultez un professionnel de santé pour un suivi
                personnalisé.
              </p>
            </div>
          </Card>

          {/* Points forts & Points d'attention */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Points forts */}
            {results.pointsForts && results.pointsForts.length > 0 && (
              <Card className="p-6 bg-green-50 dark:bg-green-950/30 border-2 border-green-200 dark:border-green-800">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <CheckCircle className="w-6 h-6 text-green-600" />✅ Points forts
                </h2>
                <ul className="space-y-2">
                  {results.pointsForts.map((point, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-2 text-gray-700 dark:text-gray-300"
                    >
                      <span className="text-green-600 font-bold">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Suivi recommandé */}
            <Card className="p-6 bg-blue-50 dark:bg-blue-950/30 border-2 border-blue-200 dark:border-blue-800">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                <Activity className="w-6 h-6 text-blue-600" />
                📅 Suivi recommandé
              </h2>
              <p className="text-gray-700 dark:text-gray-300">{results.suiviRecommande}</p>
            </Card>
          </div>

          {/* Points d'attention détaillés */}
          {results.pointsAttention && results.pointsAttention.length > 0 && (
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                ⚠️ Points d'attention
              </h2>
              <div className="space-y-4">
                {results.pointsAttention &&
                  results.pointsAttention.map((point, idx) => (
                    <Card
                      key={idx}
                      className="p-6 border-2 border-orange-200 dark:border-orange-800"
                    >
                      <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
                        {point.titre}
                      </h3>
                      <p className="text-gray-700 dark:text-gray-300 mb-3">{point.description}</p>

                      <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 mb-3">
                        <p className="text-sm font-semibold text-orange-800 dark:text-orange-300 mb-1">
                          Impact potentiel :
                        </p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{point.impact}</p>
                      </div>

                      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                        <p className="text-sm font-semibold text-green-800 dark:text-green-300 mb-2">
                          Solutions recommandées :
                        </p>
                        <ul className="space-y-1">
                          {point.solutions.map((solution, sidx) => (
                            <li
                              key={sidx}
                              className="text-sm text-gray-700 dark:text-gray-300 flex items-start gap-2"
                            >
                              <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                              <span>{solution}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </Card>
                  ))}
              </div>
            </div>
          )}

          {/* Priorités d'action */}
          {results.priorites && results.priorites.length > 0 && (
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                🎯 Plan d'action prioritaire
              </h2>
              <div className="space-y-4">
                {results.priorites.map((priorite, idx) => (
                  <Card
                    key={idx}
                    className={`p-6 border-2 ${
                      priorite.niveau.includes('Haute') || priorite.niveau.includes('URGENT')
                        ? 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/20'
                        : 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/20'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                        {priorite.titre}
                      </h3>
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-bold ${
                          priorite.niveau.includes('Haute') || priorite.niveau.includes('URGENT')
                            ? 'bg-red-200 dark:bg-red-900/50 text-red-800 dark:text-red-300'
                            : 'bg-blue-200 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300'
                        }`}
                      >
                        {priorite.niveau}
                      </span>
                    </div>

                    <p className="text-gray-700 dark:text-gray-300 mb-4">{priorite.description}</p>

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-3">
                      <p className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        Actions à mettre en place :
                      </p>
                      <ul className="space-y-2">
                        {priorite.actions.map((action, aidx) => (
                          <li
                            key={aidx}
                            className="flex items-start gap-2 text-gray-700 dark:text-gray-300"
                          >
                            <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-xs font-bold text-blue-600 dark:text-blue-400">
                                {aidx + 1}
                              </span>
                            </div>
                            <span>{action}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <Clock className="w-4 h-4" />
                      <span className="font-medium">Délai : {priorite.delai}</span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Recommandations par temporalité */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              📋 Feuille de route
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              {/* Immédiat */}
              <Card className="p-6 bg-red-50 dark:bg-red-950/20 border-2 border-red-200 dark:border-red-800">
                <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  Immédiat
                </h3>
                <ul className="space-y-2">
                  {results.recommendations?.immediates?.map((rec, idx) => (
                    <li key={idx} className="text-sm text-gray-700 dark:text-gray-300">
                      {rec}
                    </li>
                  ))}
                </ul>
              </Card>

              {/* Court terme */}
              <Card className="p-6 bg-orange-50 dark:bg-orange-950/20 border-2 border-orange-200 dark:border-orange-800">
                <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-orange-600" />
                  1-3 mois
                </h3>
                <ul className="space-y-2">
                  {results.recommendations?.courtTerme?.map((rec, idx) => (
                    <li key={idx} className="text-sm text-gray-700 dark:text-gray-300">
                      {rec}
                    </li>
                  ))}
                </ul>
              </Card>

              {/* Long terme */}
              <Card className="p-6 bg-green-50 dark:bg-green-950/20 border-2 border-green-200 dark:border-green-800">
                <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-green-600" />
                  Long terme
                </h3>
                <ul className="space-y-2">
                  {results.recommendations?.longTerme?.map((rec, idx) => (
                    <li key={idx} className="text-sm text-gray-700 dark:text-gray-300">
                      {rec}
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </div>

          {/* Actions finales */}
          <div className="grid md:grid-cols-3 gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="flex items-center gap-2  text-slate-700 dark:text-slate-300
            border-slate-300 dark:border-slate-700
            hover:bg-slate-100 dark:hover:bg-slate-800
            hover:text-slate-900 dark:hover:text-white
            transition-colors"
            >
              <MessageSquare className="w-5 h-5" />
              Poser une question
            </Button>
            <Button
              onClick={() => navigate('/daily-help')}
              variant="outline"
              className=" text-slate-700 dark:text-slate-300
            border-slate-300 dark:border-slate-700
            hover:bg-slate-100 dark:hover:bg-slate-800
            hover:text-slate-900 dark:hover:text-white
            transition-colors"
            >
              <BookOpen className="w-5 h-5" />
              Articles conseils
            </Button>
            <Button
              onClick={() => window.print()}
              variant="outline"
              className=" text-slate-700 dark:text-slate-300
            border-slate-300 dark:border-slate-700
            hover:bg-slate-100 dark:hover:bg-slate-800
            hover:text-slate-900 dark:hover:text-white
            transition-colors"
            >
              <Download className="w-5 h-5" />
              Imprimer / PDF
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return null;
};

export default QuizPage;
